import { css } from "@emotion/css";
import { Tab, Tabs, useIsUrlActive } from "@octopusdeploy/design-system-components";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import { HasRunbooksInGit, Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import type { ReactNode } from "react";
import * as React from "react";
import { useParams } from "react-router-dom";
import { useProjectContext } from "~/areas/projects/context";
import { isUrl } from "~/components/Navigation/InternalLink/isUrl";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { useUrlResolver } from "~/components/Navigation/useUrlResolver";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { withTheme } from "~/components/Theme";
import { useRunbookContext } from "../Runbooks/RunbookContext";
import { useNewRunbookRunsListPageFeatureToggle } from "./useNewRunbookRunsListPageFeatureToggle";
const projectHasTaskViewPermission = (projectId: string) => {
    return isAllowed({
        permission: Permission.TaskView,
        project: projectId,
        wildcard: true,
    });
};
const renderProcessLabelWithWarning = () => {
    return withTheme((theme) => (<div>
            Process&nbsp;
            <em className={"fa-solid fa-exclamation-triangle"} style={{ color: theme.errorText }}/>
        </div>));
};
interface NavigationTab {
    label: ReactNode;
    link: LinkHref;
    disabled?: boolean;
}
function getNavigationTabs(tabs: Array<NavigationTab | undefined>): NavigationTab[] {
    return tabs.filter(isNavigationTab);
}
function isNavigationTab(tab: NavigationTab | undefined): tab is NavigationTab {
    return Boolean(tab);
}
export default function RunbooksNavigationTabs() {
    const isUrlActive = useIsUrlActive();
    const urlResolver = useUrlResolver();
    const navigation = useSpaceAwareNavigation();
    const runbookContext = useRunbookContext();
    const projectContext = useProjectContext();
    const runbook = runbookContext.state.runbook;
    const gitRef = projectContext.state.gitRef?.CanonicalName;
    const isGitRunbook = HasRunbooksInGit(projectContext.state.model.PersistenceSettings) && !!gitRef;
    const { SpaceId: spaceId, Slug: projectSlug } = projectContext.state.model;
    // At the moment this component is used in places where we might not have loaded a runbook successfully.
    // So getting the runbookId from the runbook above could have different behaviour to getting it from the route.
    // We should more strongly define how this component should behave and where the runbookId should come from, and remove this useParams usage.
    const params = useParams<{
        runbookId?: string;
    }>();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
    const runbookId = params.runbookId!;
    const newRunbookRunsListPageEnabled = useNewRunbookRunsListPageFeatureToggle(projectContext.state.model);
    const tabs = getNavigationTabs([
        !newRunbookRunsListPageEnabled ? { label: "Overview", link: links.runbookOverviewPage.generateUrl({ spaceId, projectSlug, runbookId }) } : undefined,
        projectHasTaskViewPermission(projectContext.state.model.Id) ? { label: "Runs", link: links.runbookRunsPage.generateUrl({ spaceId, projectSlug, runbookId }, { gitRef: isGitRunbook ? gitRef : undefined }) } : undefined,
        runbook
            ? {
                label: projectContext.state.gitVariablesHasError ? renderProcessLabelWithWarning() : "Process",
                link: links.runbookProcessPage.generateUrl({ spaceId, projectSlug, runbookId, processId: runbook.RunbookProcessId }, isGitRunbook ? { gitRef } : undefined),
            }
            : undefined,
        { label: "Settings", link: links.runbookSettingsPage.generateUrl({ spaceId, projectSlug, runbookId }, { gitRef: isGitRunbook ? gitRef : undefined }) },
    ]);
    // The Tabs element gets very grumpy (logs console errors) if its 'value' property doesn't match the 'link' of one of the NavTabs, so if we can't find a match, default
    // to the whatever the first tab is. This only happens during the initial renders - later on we get the correct value and so always finds a match.
    const tabThatMatchesUrl = tabs.find((x) => isUrlActive(x.link, "if path partially matches")) || tabs[0];
    const urlOfSelectedTab = tabThatMatchesUrl.link;
    return (<div className={tabContainerStyles}>
            <Tabs value={isUrl(urlOfSelectedTab) ? urlResolver.resolve(urlOfSelectedTab) : urlOfSelectedTab} onChange={(_, val) => {
            navigation.navigate(val);
        }} variant="scrollable">
                {tabs.map((x) => {
            const link = isUrl(x.link) ? urlResolver.resolve(x.link) : x.link;
            return <Tab key={link} label={x.label} value={link} disabled={x.disabled}/>;
        })}
            </Tabs>
        </div>);
}
const tabContainerStyles = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: themeTokens.color.background.primary.default,
    borderTop: `0.0625rem solid ${themeTokens.color.border.primary}`,
    borderBottom: `0.0625rem solid ${themeTokens.color.border.primary}`,
});
